class ProductOverviewGrid extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onOutsideClick = this.onOutsideClick.bind(this);
    this.onCollapsibleChange = this.onCollapsibleChange.bind(this);
    // The feature group is an accordion on phones only, and starts collapsed.
    this.featureGroupOpen = false;
    // Must match the breakpoint the feature accordion is scoped to in
    // section-product-overview-grid.css.
    this.collapsibleQuery = window.matchMedia('(max-width: 767.98px)');
  }

  connectedCallback() {
    if (this.connected) {
      return;
    }

    this.connected = true;
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
    window.addEventListener('popstate', this.onPopState);
    document.addEventListener('click', this.onOutsideClick);
    this.collapsibleQuery.addEventListener('change', this.onCollapsibleChange);
    this.syncFeatureGroup();

    if (this.isManualSource()) {
      this.applyManualState(new URL(window.location.href), false);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('click', this.onOutsideClick);
    this.collapsibleQuery.removeEventListener('change', this.onCollapsibleChange);
    this.connected = false;
  }

  onCollapsibleChange() {
    this.syncFeatureGroup();
  }

  isFeatureGroupCollapsible() {
    return this.collapsibleQuery.matches;
  }

  setFeatureGroupOpen(isOpen) {
    this.featureGroupOpen = isOpen;
    this.syncFeatureGroup();
  }

  /* Pushes this.featureGroupOpen onto the DOM. Above the breakpoint the group
     is always expanded by CSS, so aria-expanded is reported as true there
     regardless of the stored state. Re-run after an AJAX re-render, which
     replaces the markup and would otherwise drop the shopper's open group. */
  syncFeatureGroup() {
    const group = this.querySelector('[data-filter-group="features"]');
    const toggle = this.querySelector('[data-product-overview-feature-toggle]');
    const collapsible = this.isFeatureGroupCollapsible();
    const isOpen = collapsible ? this.featureGroupOpen : true;

    if (group) {
      group.classList.toggle('is-group-open', collapsible && this.featureGroupOpen);
    }

    if (toggle) {
      toggle.setAttribute('aria-expanded', String(isOpen));
    }
  }

  onOutsideClick(event) {
    if (this.contains(event.target)) {
      return;
    }

    const openDropdown = this.querySelector('[data-product-overview-sort-dropdown].is-open');

    if (openDropdown) {
      this.closeSortDropdown(openDropdown);
    }
  }

  isManualSource() {
    return this.dataset.productOverviewSource === 'manual';
  }

  onClick(event) {
    const openDropdown = this.querySelector('[data-product-overview-sort-dropdown].is-open');

    if (openDropdown && !openDropdown.contains(event.target)) {
      this.closeSortDropdown(openDropdown);
    }

    const sortTrigger = event.target.closest('[data-product-overview-sort-trigger]');

    if (sortTrigger && this.contains(sortTrigger)) {
      event.preventDefault();
      const wrap = sortTrigger.closest('[data-product-overview-sort-dropdown]');

      if (wrap.classList.contains('is-open')) {
        this.closeSortDropdown(wrap);
      } else {
        this.openSortDropdown(wrap);
      }
      return;
    }

    const sortOption = event.target.closest('[data-product-overview-sort-option]');

    if (sortOption && this.contains(sortOption)) {
      event.preventDefault();
      this.selectSortOption(sortOption);
      return;
    }

    const featureToggle = event.target.closest('[data-product-overview-feature-toggle]');

    if (featureToggle && this.contains(featureToggle)) {
      event.preventDefault();

      // Inert above the phone breakpoint, where the group is always expanded.
      if (this.isFeatureGroupCollapsible()) {
        this.setFeatureGroupOpen(!this.featureGroupOpen);
      }

      return;
    }

    const link = event.target.closest('[data-product-overview-link], [data-product-overview-pagination] a');

    if (!link || !this.contains(link) || link.getAttribute('aria-disabled') === 'true') {
      return;
    }

    if (!link.href) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.navigate(link.href);
  }

  onChange(event) {
    const sortSelect = event.target.closest('[data-product-overview-sort]');

    if (!sortSelect || !this.contains(sortSelect)) {
      return;
    }

    const form = sortSelect.form;

    if (!form) {
      return;
    }

    const targetUrl = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);

    if (this.isManualSource()) {
      const currentUrl = new URL(window.location.href);
      const params = currentUrl.searchParams;
      const value = sortSelect.value;

      if (value && value !== 'best-selling') {
        params.set('sort_by', value);
      } else {
        params.delete('sort_by');
      }

      targetUrl.search = params.toString();
      this.navigate(targetUrl.toString());
      return;
    }

    const formData = new FormData(form);
    const params = new URLSearchParams(formData);
    targetUrl.search = params.toString();

    this.navigate(targetUrl.toString());
  }

  onPopState() {
    this.navigate(window.location.href, false);
  }

  openSortDropdown(wrap) {
    wrap.classList.add('is-open');
    const trigger = wrap.querySelector('[data-product-overview-sort-trigger]');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }
  }

  closeSortDropdown(wrap) {
    wrap.classList.remove('is-open');
    const trigger = wrap.querySelector('[data-product-overview-sort-trigger]');

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }
  }

  selectSortOption(option) {
    const wrap = option.closest('[data-product-overview-sort-dropdown]');
    const select = wrap?.querySelector('[data-product-overview-sort]');
    const value = option.dataset.value;

    if (select && value) {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (wrap) {
      this.closeSortDropdown(wrap);
    }
  }

  async navigate(url, updateHistory = true) {
    const targetUrl = new URL(url, window.location.origin);

    if (this.isManualSource()) {
      this.applyManualState(targetUrl, updateHistory);
      return;
    }

    const requestUrl = new URL(targetUrl.toString());
    const sectionId = this.dataset.sectionId;

    if (!sectionId) {
      window.location.href = targetUrl.toString();
      return;
    }

    requestUrl.searchParams.set('section_id', sectionId);
    this.classList.add('is-loading');

    try {
      const response = await fetch(requestUrl.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Product overview request failed');
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = parsed.querySelector('[data-product-overview-grid]');
      const nextInner = nextRoot?.querySelector('[data-product-overview-inner]');
      const currentInner = this.querySelector('[data-product-overview-inner]');

      if (!nextInner || !currentInner) {
        throw new Error('Product overview section response missing');
      }

      currentInner.innerHTML = nextInner.innerHTML;

      if (updateHistory) {
        history.pushState({ productOverviewGrid: true }, '', targetUrl.pathname + targetUrl.search);
      }

      this.syncFeatureGroup();
      this.scrollIntoViewIfNeeded();
    } catch (error) {
      window.location.href = targetUrl.toString();
    } finally {
      this.classList.remove('is-loading');
    }
  }

  applyManualState(targetUrl, updateHistory = true) {
    const featureParam = 'filter.p.m.custom.product_features';
    const categoryParam = 'filter.p.m.custom.product_category';
    const params = targetUrl.searchParams;
    // Features may arrive as repeated params OR one comma-joined param — the
    // server renders/reads the comma form because the monorail pageurl (the
    // only query source available to Liquid) dedupes repeated params.
    const selectedFeatures = Array.from(new Set(
      params.getAll(featureParam)
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter(Boolean)
    ));
    const selectedCategory = params.get(categoryParam) || '';
    const sortBy = params.get('sort_by') || 'best-selling';
    const items = Array.from(this.querySelectorAll('[data-product-overview-manual-item]'));
    let visibleCount = 0;

    items.forEach((item) => {
      const itemFeatures = this.parseDataList(item.dataset.features);
      const itemCategories = this.parseDataList(item.dataset.category);
      const featureMatches = selectedFeatures.length === 0 || selectedFeatures.every((feature) => itemFeatures.includes(feature));
      const categoryMatches = !selectedCategory || itemCategories.includes(selectedCategory);
      const isVisible = featureMatches && categoryMatches;

      item.hidden = !isVisible;
      item.classList.toggle('is-filtered-out', !isVisible);

      if (isVisible) {
        visibleCount += 1;
      }
    });

    this.sortManualItems(sortBy);
    this.updateManualFilterLinks(targetUrl, featureParam, categoryParam, selectedFeatures, selectedCategory);
    this.updateManualActiveChips(targetUrl, featureParam, categoryParam, selectedFeatures, selectedCategory);
    this.updateManualEmptyState(visibleCount);
    this.updateManualSort(sortBy);

    if (updateHistory) {
      history.pushState({ productOverviewGrid: true }, '', targetUrl.pathname + targetUrl.search);
      this.scrollIntoViewIfNeeded();
    }
  }

  parseDataList(value) {
    return (value || '')
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  sortManualItems(sortBy) {
    const container = this.querySelector('[data-product-overview-items]');

    if (!container) {
      return;
    }

    const items = Array.from(container.querySelectorAll('[data-product-overview-manual-item]'));
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

    items.sort((a, b) => {
      switch (sortBy) {
        case 'price-ascending':
          return Number(a.dataset.price || 0) - Number(b.dataset.price || 0);
        case 'price-descending':
          return Number(b.dataset.price || 0) - Number(a.dataset.price || 0);
        case 'title-ascending':
          return collator.compare(a.dataset.title || '', b.dataset.title || '');
        case 'title-descending':
          return collator.compare(b.dataset.title || '', a.dataset.title || '');
        case 'created-descending':
          return Number(b.dataset.created || 0) - Number(a.dataset.created || 0);
        case 'best-selling':
        default:
          return Number(a.dataset.index || 0) - Number(b.dataset.index || 0);
      }
    });

    items.forEach((item) => container.appendChild(item));
  }

  updateManualFilterLinks(targetUrl, featureParam, categoryParam, selectedFeatures, selectedCategory) {
    const featureSet = new Set(selectedFeatures);
    const pills = this.querySelectorAll('[data-product-overview-manual-filter]');

    pills.forEach((pill) => {
      const value = pill.dataset.productOverviewValue || '';
      const type = pill.dataset.productOverviewManualFilter;
      const nextUrl = new URL(targetUrl.toString());

      if (type === 'feature') {
        const nextFeatures = new Set(selectedFeatures);

        if (nextFeatures.has(value)) {
          nextFeatures.delete(value);
        } else {
          nextFeatures.add(value);
        }

        nextUrl.searchParams.delete(featureParam);
        if (nextFeatures.size > 0) {
          nextUrl.searchParams.set(featureParam, Array.from(nextFeatures).join(','));
        }
        pill.classList.toggle('is-active', featureSet.has(value));
      }

      if (type === 'category') {
        if (selectedCategory === value) {
          nextUrl.searchParams.delete(categoryParam);
        } else {
          nextUrl.searchParams.set(categoryParam, value);
        }

        pill.classList.toggle('is-active', selectedCategory === value);
      }

      pill.href = nextUrl.pathname + nextUrl.search;
    });
  }

  updateManualActiveChips(targetUrl, featureParam, categoryParam, selectedFeatures, selectedCategory) {
    const active = this.querySelector('[data-product-overview-active]');

    if (!active) {
      return;
    }

    const chips = [];

    selectedFeatures.forEach((feature) => {
      const nextUrl = new URL(targetUrl.toString());
      const nextFeatures = selectedFeatures.filter((selectedFeature) => selectedFeature !== feature);
      nextUrl.searchParams.delete(featureParam);
      if (nextFeatures.length > 0) {
        nextUrl.searchParams.set(featureParam, nextFeatures.join(','));
      }
      chips.push(this.createChip(feature, nextUrl));
    });

    if (selectedCategory) {
      const nextUrl = new URL(targetUrl.toString());
      nextUrl.searchParams.delete(categoryParam);
      chips.push(this.createChip(selectedCategory, nextUrl));
    }

    if (chips.length > 0) {
      const resetUrl = new URL(targetUrl.toString());
      resetUrl.searchParams.delete(featureParam);
      resetUrl.searchParams.delete(categoryParam);
      chips.push('<a class="product-overview-grid__reset" href="' + resetUrl.pathname + resetUrl.search + '" data-product-overview-link>Zurücksetzen</a>');
    }

    active.innerHTML = chips.join('');
    active.hidden = chips.length === 0;
  }

  createChip(label, url) {
    const safeLabel = this.escapeHtml(label);
    const closeIcon = '<svg aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="7.4" y="1" width="1.2" height="14" rx="0.6" transform="rotate(30 8 8)" fill="currentColor"/><rect x="7.4" y="1" width="1.2" height="14" rx="0.6" transform="rotate(-30 8 8)" fill="currentColor"/></svg>';
    return '<a class="product-overview-grid__active-chip" href="' + url.pathname + url.search + '" data-product-overview-link><span>' + safeLabel + '</span><span class="product-overview-grid__chip-remove" aria-hidden="true">' + closeIcon + '</span><span class="visually-hidden">Filter entfernen</span></a>';
  }

  escapeHtml(value) {
    const span = document.createElement('span');
    span.textContent = value;
    return span.innerHTML;
  }

  updateManualEmptyState(visibleCount) {
    const empty = this.querySelector('[data-product-overview-manual-empty]');

    if (empty) {
      empty.hidden = visibleCount !== 0;
    }
  }

  updateManualSort(sortBy) {
    const sortSelect = this.querySelector('[data-product-overview-sort]');

    if (sortSelect) {
      sortSelect.value = sortBy;
    }

    const wrap = this.querySelector('[data-product-overview-sort-dropdown]');

    if (!wrap) {
      return;
    }

    const trigger = wrap.querySelector('[data-product-overview-sort-trigger-label]');
    const options = wrap.querySelectorAll('[data-product-overview-sort-option]');

    options.forEach((option) => {
      const isSelected = option.dataset.value === sortBy;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-selected', String(isSelected));

      if (isSelected && trigger) {
        const labelSpan = option.querySelector('span:last-child');
        trigger.textContent = labelSpan ? labelSpan.textContent : trigger.textContent;
      }
    });
  }

  scrollIntoViewIfNeeded() {
    const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    const targetTop = this.getBoundingClientRect().top + window.scrollY - headerOffset - 16;

    if (window.scrollY > targetTop) {
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    }
  }
}

customElements.get('product-overview-grid') || customElements.define('product-overview-grid', ProductOverviewGrid);
